"use client";

import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Pagination,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

const PlanList = () => {
  const [plans, setPlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    planName: "",
    planCode: "",
    description: "",
    creditAmount: 0,
    actualPrice: 0,
    discountPercent: 0,
    validity: 365,
    features: "",
    planType: "PREPAID",
    isPopular: false,
    order: 0,
  });

  // Dummy data for initial display
  const dummyPlans = [
    {
      _id: "1",
      planName: "Starter Plan",
      planCode: "STARTER_100",
      description: "Perfect for small businesses",
      creditAmount: 100,
      actualPrice: 1000,
      discountPercent: 10,
      finalPrice: 900,
      validity: 365,
      planType: "PREPAID",
      isActive: true,
      isPopular: false,
      order: 1,
      createdAt: new Date(),
    },
    {
      _id: "2",
      planName: "Growth Plan",
      planCode: "GROWTH_500",
      description: "Ideal for growing businesses",
      creditAmount: 500,
      actualPrice: 4500,
      discountPercent: 15,
      finalPrice: 3825,
      validity: 365,
      planType: "PREPAID",
      isActive: true,
      isPopular: true,
      order: 2,
      createdAt: new Date(),
    },
    {
      _id: "3",
      planName: "Enterprise Plan",
      planCode: "ENTERPRISE_2000",
      description: "For large enterprises",
      creditAmount: 2000,
      actualPrice: 15000,
      discountPercent: 20,
      finalPrice: 12000,
      validity: 365,
      planType: "PREPAID",
      isActive: true,
      isPopular: false,
      order: 3,
      createdAt: new Date(),
    },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      // For now, using dummy data
      setPlans(dummyPlans);

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/plans`
      // );
      // const data = await response.json();
      // setPlans(data);
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlans(dummyPlans);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const filteredData = plans.filter((item) => {
    const search = searchQuery.toLowerCase();
    return (
      item.planName?.toLowerCase().includes(search) ||
      item.planCode?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleCreatePlan = async () => {
    setLoading(true);
    setError("");
    try {
      // For now, just add to dummy data
      const newPlan = {
        _id: String(plans.length + 1),
        ...formData,
        features: formData.features.split(",").map((f) => f.trim()),
        finalPrice:
          formData.actualPrice -
          (formData.actualPrice * formData.discountPercent) / 100,
        isActive: true,
        createdAt: new Date(),
      };
      setPlans([...plans, newPlan]);
      setShowCreateModal(false);
      resetForm();

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/plans`,
      //   {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({
      //       ...formData,
      //       features: formData.features.split(",").map((f) => f.trim()),
      //     }),
      //   }
      // );
      // const data = await response.json();
      // fetchPlans();
    } catch (error) {
      setError("Failed to create plan");
      console.error("Error creating plan:", error);
    }
    setLoading(false);
  };

  const handleEditPlan = async () => {
    setLoading(true);
    setError("");
    try {
      // For now, just update dummy data
      const updatedPlans = plans.map((plan) =>
        plan._id === selectedPlan._id
          ? {
              ...plan,
              ...formData,
              features: formData.features.split(",").map((f) => f.trim()),
              finalPrice:
                formData.actualPrice -
                (formData.actualPrice * formData.discountPercent) / 100,
            }
          : plan,
      );
      setPlans(updatedPlans);
      setShowEditModal(false);
      resetForm();

      // Uncomment when backend is ready
      // const response = await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/plans/${selectedPlan._id}`,
      //   {
      //     method: "PUT",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({
      //       ...formData,
      //       features: formData.features.split(",").map((f) => f.trim()),
      //     }),
      //   }
      // );
      // fetchPlans();
    } catch (error) {
      setError("Failed to update plan");
      console.error("Error updating plan:", error);
    }
    setLoading(false);
  };

  const handleDeletePlan = async (id) => {
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        // For now, just remove from dummy data
        setPlans(plans.filter((plan) => plan._id !== id));

        // Uncomment when backend is ready
        // await fetch(
        //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/plans/${id}`,
        //   {
        //     method: "DELETE",
        //   }
        // );
        // fetchPlans();
      } catch (error) {
        console.error("Error deleting plan:", error);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      // For now, just toggle in dummy data
      const updatedPlans = plans.map((plan) =>
        plan._id === id ? { ...plan, isActive: !plan.isActive } : plan,
      );
      setPlans(updatedPlans);

      // Uncomment when backend is ready
      // await fetch(
      //   `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/plans/${id}/toggle-status`,
      //   {
      //     method: "PATCH",
      //   }
      // );
      // fetchPlans();
    } catch (error) {
      console.error("Error toggling plan status:", error);
    }
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      planName: plan.planName,
      planCode: plan.planCode,
      description: plan.description,
      creditAmount: plan.creditAmount,
      actualPrice: plan.actualPrice,
      discountPercent: plan.discountPercent,
      validity: plan.validity,
      features: Array.isArray(plan.features)
        ? plan.features.join(", ")
        : plan.features || "",
      planType: plan.planType,
      isPopular: plan.isPopular,
      order: plan.order,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      planName: "",
      planCode: "",
      description: "",
      creditAmount: 0,
      actualPrice: 0,
      discountPercent: 0,
      validity: 365,
      features: "",
      planType: "PREPAID",
      isPopular: false,
      order: 0,
    });
    setSelectedPlan(null);
    setError("");
  };

  const PlanForm = () => (
    <Box mt={2}>
      <TextField
        label="Plan Name *"
        fullWidth
        margin="normal"
        value={formData.planName}
        onChange={(e) =>
          setFormData((f) => ({ ...f, planName: e.target.value }))
        }
      />
      <TextField
        label="Plan Code *"
        fullWidth
        margin="normal"
        value={formData.planCode}
        onChange={(e) =>
          setFormData((f) => ({ ...f, planCode: e.target.value.toUpperCase() }))
        }
        placeholder="e.g., STARTER_100"
      />
      <TextField
        label="Description"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={formData.description}
        onChange={(e) =>
          setFormData((f) => ({ ...f, description: e.target.value }))
        }
      />
      <TextField
        label="Credit Amount *"
        fullWidth
        margin="normal"
        type="number"
        value={formData.creditAmount}
        onChange={(e) =>
          setFormData((f) => ({ ...f, creditAmount: Number(e.target.value) }))
        }
      />
      <TextField
        label="Actual Price (₹) *"
        fullWidth
        margin="normal"
        type="number"
        value={formData.actualPrice}
        onChange={(e) =>
          setFormData((f) => ({ ...f, actualPrice: Number(e.target.value) }))
        }
      />
      <TextField
        label="Discount Percent (%)"
        fullWidth
        margin="normal"
        type="number"
        value={formData.discountPercent}
        onChange={(e) =>
          setFormData((f) => ({
            ...f,
            discountPercent: Number(e.target.value),
          }))
        }
      />
      <TextField
        label="Validity (Days)"
        fullWidth
        margin="normal"
        type="number"
        value={formData.validity}
        onChange={(e) =>
          setFormData((f) => ({ ...f, validity: Number(e.target.value) }))
        }
      />
      <TextField
        label="Features (comma separated)"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={formData.features}
        onChange={(e) =>
          setFormData((f) => ({ ...f, features: e.target.value }))
        }
        placeholder="Feature 1, Feature 2, Feature 3"
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Plan Type</InputLabel>
        <Select
          value={formData.planType}
          onChange={(e) =>
            setFormData((f) => ({ ...f, planType: e.target.value }))
          }
          label="Plan Type"
        >
          <MenuItem value="PREPAID">Prepaid</MenuItem>
          <MenuItem value="POSTPAID">Postpaid</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Display Order"
        fullWidth
        margin="normal"
        type="number"
        value={formData.order}
        onChange={(e) =>
          setFormData((f) => ({ ...f, order: Number(e.target.value) }))
        }
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Mark as Popular</InputLabel>
        <Select
          value={formData.isPopular ? "yes" : "no"}
          onChange={(e) =>
            setFormData((f) => ({ ...f, isPopular: e.target.value === "yes" }))
          }
          label="Mark as Popular"
        >
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="body2" color="textSecondary" mt={2}>
        Final Price: ₹
        {(
          formData.actualPrice -
          (formData.actualPrice * formData.discountPercent) / 100
        ).toFixed(2)}
      </Typography>
      {error && (
        <Typography color="error" mt={2}>
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" component="h2">
            WhatsApp Plans Management
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
            >
              Add New Plan
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Plan Name</TableCell>
                <TableCell>Plan Code</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Final Price</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((plan) => (
                  <TableRow key={plan._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {plan.planName}
                        {plan.isPopular && (
                          <Chip label="Popular" color="primary" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {plan.planCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{plan.creditAmount}</TableCell>
                    <TableCell>₹{plan.actualPrice}</TableCell>
                    <TableCell>{plan.discountPercent}%</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                      >
                        ₹{plan.finalPrice}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.planType}
                        size="small"
                        color={
                          plan.planType === "PREPAID" ? "success" : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={plan.isActive ? "success" : "error"}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditModal(plan)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(plan._id)}
                        color={plan.isActive ? "success" : "default"}
                      >
                        {plan.isActive ? (
                          <ToggleOnIcon fontSize="small" />
                        ) : (
                          <ToggleOffIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePlan(plan._id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No plans found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="body2">
            Showing {paginatedData.length} of {filteredData.length} items
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
            />
          )}
        </Box>

        {/* Create Plan Modal */}
        <Dialog
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Plan</DialogTitle>
          <DialogContent>
            <PlanForm />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreatePlan}
              disabled={loading}
            >
              Create Plan
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Plan Modal */}
        <Dialog
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Plan</DialogTitle>
          <DialogContent>
            <PlanForm />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditPlan}
              disabled={loading}
            >
              Update Plan
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PlanList;
